/**
 * Autonomous Discovery Mode
 *
 * Two-phase flow:
 * Phase 1 (Silent Exploration ~5min): Agent autonomously navigates the product
 *   in an iframe, captures snapshots, builds a draft PDP, records findings.
 * Phase 2 (Smart Q&A): Agent presents a summary, then asks questions one-by-one,
 *   navigating to the relevant page for each question so the user sees context.
 */

// ─── Types ────────────────────────────────────────────────────────────────

interface DAMessage {
  __da: true;
  id: string;
  gen: number;
  ts: number;
  dir: 'parent-to-iframe' | 'iframe-to-parent';
  type: string;
  payload: Record<string, unknown>;
}

export interface AutonomousDiscoveryConfig {
  apiUrl: string;
  apiKey: string;
  sessionId: string;
  token: string;
  productUrl: string;
}

interface PageSnapshot {
  urlPath: string;
  pageTitle: string;
  headings: string[];
  elements: Array<Record<string, unknown>>;
  navigation: Array<{ text: string; href: string }>;
  forms: Array<Record<string, unknown>>;
  tables: Array<Record<string, unknown>>;
  viewport: { width: number; height: number };
}

interface ExploreAction {
  type: 'click' | 'navigate' | 'scroll_to' | 'type' | 'press_key' | 'wait';
  selector?: string;
  url?: string;
  text?: string;
  key?: string;
  ms?: number;
  description: string;
}

interface Finding {
  type: string;
  description: string;
  page: string;
  importance: string;
  needsUserInput: boolean;
  questionHint?: string;
}

interface Question {
  id: string;
  question: string;
  context: string;
  relatedPage: string;
  relatedElementSelectors?: string[];
  pdpField: string;
  priority: number;
}

type Phase = 'scanning' | 'deep_diving' | 'summarizing' | 'questions' | 'finalizing' | 'complete';
type ExploreState = 'running' | 'paused' | 'manual_takeover';

interface PageScore {
  urlPath: string;
  title: string;
  summary: string;
  score: number;
  category: 'high' | 'medium' | 'low' | 'skip';
  reasons: string[];
  unexploredInteractions: string[];
}

// ─── Main Class ───────────────────────────────────────────────────────────

export class AutonomousDiscoveryMode {
  private config: AutonomousDiscoveryConfig;

  // Phase tracking
  private phase: Phase = 'scanning';
  private exploreState: ExploreState = 'running';

  // DOM refs
  private container: HTMLDivElement | null = null;
  private shadow: ShadowRoot | null = null;
  private iframe: HTMLIFrameElement | null = null;
  private messagesEl: HTMLElement | null = null;
  private inputArea: HTMLElement | null = null;
  private inputEl: HTMLTextAreaElement | null = null;
  private statusEl: HTMLElement | null = null;
  private progressEl: HTMLElement | null = null;
  private pageListEl: HTMLElement | null = null;
  private controlsEl: HTMLElement | null = null;

  // Exploration state
  private exploredPages = new Map<string, { title: string; elementCount: number; summary: string }>();
  private pendingNavTargets: Array<{ text: string; href: string }> = [];
  private findings: Finding[] = [];
  private draftPdp: Record<string, unknown> = {};
  private iterationCount = 0;
  private startTime = 0;

  // Two-pass state
  private pageScores: PageScore[] = [];
  private deepDiveTargets: string[] = [];
  private deepDiveCurrentPage = '';
  private deepDiveIterationsOnPage = 0;
  private deepDiveCompletedPages = new Set<string>();

  // Q&A state
  private questions: Question[] = [];
  private currentQuestionIndex = 0;
  private userResponseResolve: ((text: string) => void) | null = null;

  // Limits — increased total budget for two-pass
  private maxExploreTimeMs = 8 * 60 * 1000;    // 8 min total (was 5)
  private maxScanIterations = 25;                // scan phase cap
  private maxDeepDiveIterations = 50;            // deep-dive phase cap
  private maxIterations = 75;                    // absolute safety cap
  private iterationDelayMs = 1200;

  // URL pattern deduplication (e.g., /users/1 and /users/2 → /users/:id)
  private exploredPatterns = new Set<string>();

  // Differential context tracking
  private lastSyncedFindingIndex = 0;

  // Debug data from DOM inspection
  private lastDebugData: Record<string, unknown> | null = null;

  // Error tracking
  private consecutiveSnapshotErrors = 0;
  private consecutiveApiErrors = 0;
  private consecutiveEmptyActions = 0;

  // Iframe communication
  private commandIdCounter = 0;
  private currentGen = 0;
  private pendingCommands = new Map<string, {
    resolve: (data: unknown) => void;
    reject: (err: Error) => void;
    timer: ReturnType<typeof setTimeout>;
  }>();
  private iframeReady = false;
  private readyResolve: (() => void) | null = null;

  constructor(config: AutonomousDiscoveryConfig) {
    this.config = config;
  }

  // ─── Lifecycle ──────────────────────────────────────────────────────────

  async start(): Promise<void> {
    this.renderLayout();
    this.setupIframeListener();
    this.loadProductInIframe();

    // Wait for iframe to report ready
    this.updateStatus('Waiting for product to load...');
    try {
      await this.waitForIframeReady(15000);
    } catch {
      this.addSystemMessage('Could not connect to your product. It may block iframe embedding. Please use Manual Discovery instead, or update your CSP to allow framing from iraa.ai.');
      return;
    }

    this.updateStatus('Product loaded. Verifying communication...');
    this.startTime = Date.now();

    // Pre-flight: verify iframe communication works before entering the loop
    try {
      await this.captureSnapshot();
      this.updateStatus('Communication verified. Starting exploration...');
    } catch {
      this.addSystemMessage(
        'Cannot communicate with the product iframe. This usually means the agent script ' +
        'is not loaded inside the iframe, or the product blocks cross-origin postMessage. ' +
        'Try Manual Discovery instead.',
      );
      this.updateStatus('Communication failed');
      return;
    }

    await this.delay(1000);
    await this.exploreLoop();
  }

  stop(): void {
    window.removeEventListener('message', this.handleIframeMessage);
    for (const pending of this.pendingCommands.values()) {
      clearTimeout(pending.timer);
      pending.reject(new Error('Stopped'));
    }
    this.pendingCommands.clear();
    this.container?.remove();
  }

  // ─── Phase 0: Client-Side Crawl + AI Planning ───────────────────────────

  private async exploreLoop(): Promise<void> {
    // PHASE 0: Client-side crawl — discover all pages with zero API calls
    this.phase = 'scanning';
    this.updateControls();
    this.addAgentMessage('Mapping all pages...');

    const siteMap = await this.runClientSideCrawl();

    if (siteMap.length === 0) {
      this.addSystemMessage('Could not discover any pages. Try Manual Discovery.');
      await this.transitionToQuestions();
      return;
    }

    this.addAgentMessage(`Found ${siteMap.length} pages. Analyzing product structure...`);

    // PHASE 1: AI planning — one API call replaces 25 scan iterations
    // Always run the plan even if paused — we want the page data populated
    let plan;
    try {
      plan = await this.callAutoPlan(siteMap);
    } catch (err) {
      console.warn('[AutoDiscovery] Auto-plan failed:', err);
      this.addSystemMessage('Failed to analyze product structure. Moving to questions with what we have.');
      await this.transitionToQuestions();
      return;
    }

    // Populate state from the AI plan BEFORE checking pause state
    this.pageScores = plan.pageScores;
    this.deepDiveTargets = plan.deepDiveTargets;
    this.draftPdp = plan.draftPdp;
    for (const ps of plan.pageScores) {
      this.exploredPages.set(ps.urlPath, {
        title: ps.title,
        elementCount: 0,
        summary: ps.summary,
      });
      this.exploredPatterns.add(this.normalizeUrlPattern(ps.urlPath));
    }
    this.updatePageList();

    const highCount = plan.pageScores.filter((p) => p.category === 'high' || p.category === 'medium').length;
    this.addAgentMessage(
      `Analyzed ${siteMap.length} pages. ${highCount} are high-value.\n` +
      `Product: ${plan.productType.category} — ${plan.productType.coreValueProp}\n` +
      `Deep-diving into ${this.deepDiveTargets.length} key pages...`,
    );

    // Check if paused/stopped AFTER plan data is populated
    if (this.exploreState !== 'running') {
      await this.transitionToQuestions();
      return;
    }

    // PHASE 2: Deep dive — full snapshots only on selected pages
    if (this.deepDiveTargets.length > 0) {
      this.phase = 'deep_diving';
      this.updateControls();
      await this.runExplorationLoop('deep_dive', this.maxDeepDiveIterations);
    }

    // PHASE 3: Q&A
    await this.transitionToQuestions();
  }

  // ─── Client-Side Crawl (zero API calls) ─────────────────────────────────

  private async runClientSideCrawl(): Promise<Array<Record<string, unknown>>> {
    const visited = new Set<string>();
    const siteMap: Array<Record<string, unknown>> = [];
    const queue: Array<{ href: string; text: string }> = [];
    const maxPages = 60;

    // Wait for the page to fully render before capturing
    await this.delay(1500);

    // Run DOM debug to understand what's on the page
    try {
      const debug = await this.sendIframeCommand('da:capture-dom-debug', {}, 5000) as Record<string, unknown>;
      console.log(`[AutoDiscovery] DOM Debug: ${debug.totalElements} elements, ${debug.totalAnchors} <a> (${debug.anchorsWithHref} with href), ${debug.clickableCount} cursor:pointer`);
      console.log(`[AutoDiscovery] Anchors:`, JSON.stringify(debug.anchorDetails));
      console.log(`[AutoDiscovery] Clickables:`, JSON.stringify((debug.clickables as unknown[])?.slice(0, 25)));
      // Store for server logging
      this.lastDebugData = debug;
    } catch {
      console.warn('[AutoDiscovery] DOM debug failed');
    }

    // Start with current page
    try {
      const fp = await this.sendIframeCommand('da:capture-fingerprint', {}, 5000) as Record<string, unknown>;
      siteMap.push(fp);
      visited.add(fp.url as string);
      const links = (fp.links as Array<{ text: string; href: string }>) ?? [];
      // Prioritize <a href> links (safer) before click: buttons
      const hrefLinks = links.filter((l) => !l.href.startsWith('click:'));
      const clickLinks = links.filter((l) => l.href.startsWith('click:'));
      console.log(`[AutoDiscovery] Crawl: first page "${fp.url}" has ${hrefLinks.length} href links + ${clickLinks.length} click targets`);
      console.log(`[AutoDiscovery] Crawl links:`, links.slice(0, 20).map((l) => `${l.text}→${l.href.slice(0, 60)}`));
      for (const link of hrefLinks) queue.push(link);
      for (const link of clickLinks) queue.push(link);
      this.updateStatus(`Crawling: found ${siteMap.length} pages...`);
    } catch (err) {
      console.warn('[AutoDiscovery] First fingerprint failed:', err);
      return siteMap;
    }

    // BFS crawl through all discoverable pages
    while (queue.length > 0 && siteMap.length < maxPages && this.exploreState === 'running') {
      const next = queue.shift()!;
      const href = next.href;

      // Resolve and deduplicate
      let targetKey: string;
      if (href.startsWith('click:')) {
        targetKey = `click:${next.text}`;
      } else {
        try {
          const resolvedPath = new URL(this.resolveUrl(href)).pathname;
          targetKey = this.normalizeUrlPattern(resolvedPath);
        } catch { continue; }
      }
      if (visited.has(targetKey)) continue;
      visited.add(targetKey);

      // Navigate to the page
      try {
        if (href.startsWith('click:')) {
          const selector = href.slice(6);
          await this.sendIframeCommand('da:click', { selector, cursorAnimate: false }, 3000);
          await this.delay(1500);
        } else {
          const url = this.resolveUrl(href);
          await this.sendIframeCommand('da:navigate', { url }, 5000);
          this.iframeReady = false;
          try { await this.waitForIframeReady(5000); } catch { await this.delay(1500); }
        }
      } catch {
        console.warn(`[AutoDiscovery] Crawl: could not navigate to "${next.text}" (${href})`);
        continue;
      }

      // Capture lightweight fingerprint
      try {
        const fp = await this.sendIframeCommand('da:capture-fingerprint', {}, 3000) as Record<string, unknown>;
        const fpUrl = fp.url as string;

        // Skip if we already captured this actual URL (SPA may not have navigated)
        if (siteMap.some((p) => p.url === fpUrl)) continue;

        siteMap.push(fp);

        // Add new links to queue — href links first (safer), then click targets
        const newLinks = (fp.links as Array<{ text: string; href: string }>) ?? [];
        for (const link of newLinks.filter((l) => !l.href.startsWith('click:'))) queue.push(link);
        for (const link of newLinks.filter((l) => l.href.startsWith('click:'))) queue.push(link);

        this.updateStatus(`Crawling: found ${siteMap.length} pages... (${Math.min(queue.length, 20)} queued)`);

        // Update stats counter live during crawl
        const statsEl = this.shadow?.querySelector('.da-auto-stats');
        if (statsEl) {
          statsEl.innerHTML = `
            <span class="da-auto-stat">Pages: ${siteMap.length}</span>
            <span class="da-auto-stat">Elements: 0</span>
            <span class="da-auto-stat">Findings: 0</span>
          `;
        }
      } catch {
        console.warn(`[AutoDiscovery] Crawl: fingerprint failed for "${next.text}"`);
        continue;
      }

      await this.delay(200); // Brief pause between pages
    }

    console.log(`[AutoDiscovery] Crawl complete: ${siteMap.length} pages discovered`);
    return siteMap;
  }

  // ─── AI Planning API Call ───────────────────────────────────────────────

  private async callAutoPlan(siteMap: Array<Record<string, unknown>>): Promise<{
    productType: { category: string; primaryPersona: string; coreValueProp: string };
    pageScores: PageScore[];
    deepDiveTargets: string[];
    draftPdp: Record<string, unknown>;
  }> {
    const resp = await fetch(`${this.config.apiUrl}/api/v1/discover/auto-plan`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: this.config.sessionId,
        token: this.config.token,
        siteMap,
        domDebug: this.lastDebugData,
      }),
    });

    if (!resp.ok) {
      let errorDetail = `${resp.status}`;
      try {
        const body = await resp.json();
        if (body.error) errorDetail = `${resp.status}: ${body.error}`;
      } catch { /* not JSON */ }
      throw new Error(`Auto-plan failed (${errorDetail})`);
    }

    return resp.json();
  }

  /**
   * Shared exploration loop used by both scan and deep-dive phases.
   * Returns true if the loop completed normally (not interrupted).
   */
  private async runExplorationLoop(
    explorationPhase: 'scan' | 'deep_dive',
    maxPhaseIterations: number,
  ): Promise<boolean> {
    let phaseIterations = 0;

    while (this.exploreState === 'running' && phaseIterations < maxPhaseIterations && this.iterationCount < this.maxIterations) {
      const elapsed = Date.now() - this.startTime;
      if (elapsed > this.maxExploreTimeMs) {
        this.updateStatus('Exploration time reached. Wrapping up...');
        return false;
      }

      // Update timer
      const mins = Math.floor(elapsed / 60000);
      const secs = Math.floor((elapsed % 60000) / 1000);
      this.updateProgress(mins, secs);

      // Phase-specific status
      if (explorationPhase === 'scan') {
        this.updateStatus(`Scanning page ${this.exploredPages.size + 1}... (${this.pendingNavTargets.length} remaining)`);
      } else {
        const remaining = this.deepDiveTargets.length - this.deepDiveCompletedPages.size;
        this.updateStatus(`Deep-diving: ${this.deepDiveCurrentPage || 'navigating'} (${remaining} pages left)`);
      }

      // 1. Capture snapshot from iframe
      let snapshot: PageSnapshot;
      try {
        snapshot = await this.captureSnapshot();
        this.consecutiveSnapshotErrors = 0;
      } catch (snapshotErr) {
        this.consecutiveSnapshotErrors++;
        const attempt = this.consecutiveSnapshotErrors;
        const max = 3; // Reduced from 5 — fail faster
        console.warn(`[AutoDiscovery] Snapshot failed (attempt ${attempt}/${max}):`, snapshotErr);

        if (attempt >= max) {
          // During scan: skip this page and try the next nav target instead of stopping
          if (explorationPhase === 'scan' && this.pendingNavTargets.length > 0) {
            const skipped = this.pendingNavTargets.shift();
            console.warn(`[AutoDiscovery] Skipping unreachable page, trying next target: ${skipped?.text}`);
            this.addSystemMessage(`Couldn't reach page — skipping to next.`);
            this.consecutiveSnapshotErrors = 0;

            // Try navigating to the next target
            const next = this.pendingNavTargets[0];
            if (next) {
              try {
                if (next.href.startsWith('click:')) {
                  const selector = next.href.slice(6);
                  await this.sendIframeCommand('da:click', { selector, cursorAnimate: false }, 5000).catch(() => {});
                } else {
                  const url = this.resolveUrl(next.href);
                  await this.sendIframeCommand('da:navigate', { url }).catch(() => {});
                }
                this.iframeReady = false;
                try { await this.waitForIframeReady(8000); } catch { await this.delay(2000); }
              } catch {
                // Navigation also failed
              }
            }
            continue;
          }

          this.addSystemMessage(
            `Cannot communicate with the product iframe after ${max} attempts. ` +
            'The product may not have the agent script installed, or it may block postMessage. ' +
            'Try refreshing the page or using Manual Discovery.',
          );
          this.updateStatus('Exploration stopped: iframe communication failed');
          return false;
        }
        await this.delay(2000);
        continue;
      }

      // 2. Update pending nav targets from snapshot
      this.updateNavTargets(snapshot);

      // Track deep-dive page context
      if (explorationPhase === 'deep_dive') {
        const currentPath = snapshot.urlPath;
        if (currentPath !== this.deepDiveCurrentPage) {
          this.deepDiveCurrentPage = currentPath;
          this.deepDiveIterationsOnPage = 0;
        }
        this.deepDiveIterationsOnPage++;
      }

      // 3. Call server with phase info
      let result: Record<string, unknown>;
      try {
        result = await this.callAutoExplore(snapshot, explorationPhase);
        this.consecutiveApiErrors = 0;
      } catch (err) {
        this.consecutiveApiErrors++;
        const errMsg = err instanceof Error ? err.message : String(err);
        console.warn('[AutoDiscovery] Explore step failed:', errMsg);

        // Fatal errors: stop immediately
        if (errMsg.includes('401')) {
          this.addSystemMessage('Session expired or invalid. Please restart discovery from the dashboard.');
          this.updateStatus('Exploration stopped: session invalid');
          return false;
        }
        if (errMsg.includes('429')) {
          this.addSystemMessage('Token budget exceeded for this session. Moving to questions with what we have.');
          this.updateStatus('Token budget reached');
          return false;
        }

        const attempt = this.consecutiveApiErrors;
        const max = 3;
        this.updateStatus(`Exploration API error, retrying... (${attempt}/${max})`);

        if (attempt >= max) {
          this.addSystemMessage(
            `Exploration API failed ${max} times: ${errMsg}. ` +
            'Moving to the question phase with what was discovered so far.',
          );
          this.updateStatus('Exploration stopped: API errors');
          return false;
        }
        await this.delay(3000);
        continue;
      }

      // 4. Execute actions with visual feedback
      const actions = (result.actions as ExploreAction[]) || [];
      const newFindings = (result.findings as Finding[]) || [];

      if (actions.length === 0) {
        this.consecutiveEmptyActions++;
        if (newFindings.length > 0) {
          this.updateStatus(`Analyzing page... (${newFindings.length} findings)`);
        } else {
          this.updateStatus('Thinking...');
        }
        if (this.consecutiveEmptyActions >= 3) {
          if (explorationPhase === 'scan') {
            this.addSystemMessage('Scan stalled — moving to deep dive with what was discovered.');
          } else {
            this.addSystemMessage('Deep dive stalled — moving to questions.');
          }
          return false;
        }
      } else {
        this.consecutiveEmptyActions = 0;
      }

      console.log(`[AutoDiscovery] iter=${this.iterationCount} phase=${explorationPhase} url=${snapshot.urlPath} actions=${actions.length} findings=${newFindings.length} navTargets=${this.pendingNavTargets.length}`);

      for (const action of actions) {
        if (this.exploreState !== 'running') break;

        // Safety net: auto-close open modals before scroll/navigate actions
        if ((action.type === 'scroll_to' || action.type === 'navigate') && (snapshot as unknown as Record<string, unknown>).openModals && ((snapshot as unknown as Record<string, unknown>).openModals as unknown[]).length > 0) {
          try {
            await this.sendIframeCommand('da:press-key', { key: 'Escape' }, 2000);
            await this.delay(500);
          } catch { /* ignore */ }
        }

        this.updateStatus(action.description);
        console.log(`[AutoDiscovery] Executing: ${action.type} ${action.type === 'navigate' ? action.url : action.selector || ''}`);
        try {
          await this.executeAction(action, snapshot);
          console.log(`[AutoDiscovery] Action completed: ${action.type}`);
        } catch (err) {
          const errMsg = err instanceof Error ? err.message : String(err);
          console.warn('[AutoDiscovery] Action failed:', errMsg);
          this.updateStatus(`Action failed: ${action.description} — continuing...`);
        }
        await this.delay(800);
      }

      // 5. Update local state from result
      this.lastSyncedFindingIndex = this.findings.length;
      this.findings.push(...newFindings);

      if (result.draftPdp) {
        this.draftPdp = result.draftPdp as Record<string, unknown>;
      }

      if (result.pageComplete && result.pageSummary) {
        const urlPath = snapshot.urlPath;
        this.exploredPages.set(urlPath, {
          title: snapshot.pageTitle,
          elementCount: snapshot.elements.length,
          summary: result.pageSummary as string,
        });
        this.exploredPatterns.add(this.normalizeUrlPattern(urlPath));
        this.removePendingTarget(urlPath);
        this.updatePageList();

        // Collect page score from scan phase
        if (result.pageScore) {
          const existing = this.pageScores.findIndex((p) => p.urlPath === urlPath);
          if (existing >= 0) {
            this.pageScores[existing] = result.pageScore as PageScore;
          } else {
            this.pageScores.push(result.pageScore as PageScore);
          }
        }

        // Track deep-dive page completion
        if (explorationPhase === 'deep_dive' && this.deepDiveTargets.includes(urlPath)) {
          this.deepDiveCompletedPages.add(urlPath);
        }
      }

      // 6. Check if exploration phase is complete
      if (result.explorationComplete) {
        if (result.questions) {
          this.questions = result.questions as Question[];
        }
        return true;
      }

      this.iterationCount++;
      phaseIterations++;
      await this.delay(this.iterationDelayMs);
    }

    return true;
  }

  // ─── Phase 1→2 Transition ──────────────────────────────────────────────

  private async transitionToQuestions(): Promise<void> {
    this.phase = 'summarizing';

    // If we have no questions yet, request them from the server
    if (this.questions.length === 0 && (this.findings.length > 0 || this.exploredPages.size > 0)) {
      this.updateStatus('Generating questions from findings...');
      try {
        const questions = await this.callGenerateQuestions();
        if (questions.length > 0) {
          this.questions = questions;
        }
      } catch (err) {
        console.warn('[AutoDiscovery] Failed to generate questions:', err);
      }
    }

    const pagesCount = this.exploredPages.size;
    const elemCount = Array.from(this.exploredPages.values()).reduce((sum, p) => sum + p.elementCount, 0);
    const featureCount = this.findings.filter((f) => f.type === 'feature').length;
    const formCount = this.findings.filter((f) => f.type === 'form').length;
    const deepDivedCount = this.deepDiveCompletedPages.size;

    if (this.questions.length > 0) {
      let summary = `I scanned ${pagesCount} pages with ${elemCount} interactive elements.`;
      if (deepDivedCount > 0) {
        summary += ` Deep-dived into ${deepDivedCount} high-value pages.`;
      }
      summary += ` Found ${featureCount} key features and ${formCount} forms.\n\nI have ${this.questions.length} questions to make your demo configuration perfect.`;
      this.addAgentMessage(summary);
      this.updateStatus('Ready for questions');
    } else {
      this.addAgentMessage(`Scanned ${pagesCount} pages, deep-dived ${deepDivedCount}. Found ${featureCount} features and ${formCount} forms. Generating your configuration now...`);
      this.updateStatus('Finalizing...');
    }

    this.phase = 'questions';
    this.currentQuestionIndex = 0;
    this.updateControls();
    this.showInputArea();

    await this.delay(2000);
    await this.askNextQuestion();
  }

  // ─── Phase 2: Question-by-Question ─────────────────────────────────────

  private async askNextQuestion(): Promise<void> {
    if (this.currentQuestionIndex >= this.questions.length) {
      await this.handleFinalize();
      return;
    }

    const question = this.questions[this.currentQuestionIndex];
    this.updateStatus(`Question ${this.currentQuestionIndex + 1} of ${this.questions.length}`);

    // Navigate iframe to relevant page
    if (question.relatedPage) {
      try {
        const url = this.resolveUrl(question.relatedPage);
        await this.sendIframeCommand('da:navigate', { url });
        await this.delay(1500);
      } catch {
        // Navigation failed, continue anyway
      }
    }

    // Highlight relevant elements
    if (question.relatedElementSelectors?.length) {
      for (const selector of question.relatedElementSelectors) {
        try {
          await this.sendIframeCommand('da:highlight', { selector, style: 'pulse', duration: 0 });
        } catch {
          // Element might not exist anymore
        }
      }
    }

    // Show question
    this.addAgentMessage(question.question);

    // Wait for user response
    const answer = await this.waitForUserInput();

    // Dismiss highlights
    try {
      await this.sendIframeCommand('da:dismiss-highlights', {});
    } catch { /* ignore */ }

    // Call server to refine PDP
    try {
      const result = await this.callAutoRefine(question.id, answer);
      if (result.draftPdp) {
        this.draftPdp = result.draftPdp as Record<string, unknown>;
      }

      if (result.followUp) {
        this.addAgentMessage(result.followUp as string);
        const followUpAnswer = await this.waitForUserInput();
        // Send follow-up as another refine call
        await this.callAutoRefine(question.id + '_followup', followUpAnswer);
      }
    } catch (err) {
      console.warn('[AutoDiscovery] Refine failed:', err);
    }

    this.currentQuestionIndex++;
    await this.askNextQuestion();
  }

  // ─── Finalize ───────────────────────────────────────────────────────────

  private async handleFinalize(): Promise<void> {
    this.phase = 'finalizing';
    this.updateStatus('Generating your demo configuration...');
    this.updateControls();
    this.addAgentMessage('Generating your demo configuration. This takes about 30 seconds...');

    try {
      const resp = await fetch(`${this.config.apiUrl}/api/v1/discover/finalize`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          sessionId: this.config.sessionId,
          token: this.config.token,
        }),
      });

      if (!resp.ok) {
        const err = await resp.json().catch(() => ({ error: 'Unknown error' }));
        throw new Error((err as Record<string, string>).error || 'Finalize failed');
      }

      this.phase = 'complete';
      this.updateStatus('Done!');
      this.addAgentMessage('Your demo configuration is ready! Head back to your dashboard to review and customize it.');
      this.updateControls();
    } catch (err) {
      this.addSystemMessage(`Failed to generate configuration: ${err instanceof Error ? err.message : 'Unknown error'}. You can try clicking Finish again.`);
      this.phase = 'questions';
      this.updateControls();
    }
  }

  // ─── User Input Handling ────────────────────────────────────────────────

  private waitForUserInput(): Promise<string> {
    return new Promise((resolve) => {
      this.userResponseResolve = resolve;
    });
  }

  private handleUserSend(): void {
    if (!this.inputEl) return;
    const text = this.inputEl.value.trim();
    if (!text) return;

    this.inputEl.value = '';
    this.addUserMessage(text);

    if (this.userResponseResolve) {
      const resolve = this.userResponseResolve;
      this.userResponseResolve = null;
      resolve(text);
    }
  }

  // ─── Iframe Communication ──────────────────────────────────────────────

  private setupIframeListener(): void {
    window.addEventListener('message', this.handleIframeMessage);
  }

  private handleIframeMessage = (event: MessageEvent): void => {
    const data = event.data;
    if (!data || typeof data !== 'object') return;

    // Only accept messages from our own iframe — see full-page-demo for rationale.
    const expectedSource = this.iframe?.contentWindow ?? null;
    if (expectedSource && event.source !== expectedSource) return;

    // Iframe-side boot fallback: when window.name didn't reach the inner
    // page (sandbox/credentialless quirk, or cross-origin redirect that
    // reset the browsing-context name), the child posts this and we reply
    // with the token it would have read from window.name. See
    // requestIframeTokenFromParent in src/index.ts.
    if ((data as { __iraa?: string }).__iraa === 'iframe-boot-request') {
      this.iframe?.contentWindow?.postMessage(
        { __iraa: 'iframe-init', kind: 'da_auto_discover_iframe', token: this.config.token },
        '*',
      );
      return;
    }

    if (!data.__da || data.dir !== 'iframe-to-parent') return;

    const msg = data as DAMessage;

    if (msg.type === 'da:ready') {
      this.iframeReady = true;
      this.readyResolve?.();
      return;
    }

    if (msg.type === 'da:cmd-result') {
      const pending = this.pendingCommands.get(msg.payload.id as string);
      if (pending) {
        clearTimeout(pending.timer);
        this.pendingCommands.delete(msg.payload.id as string);
        if (msg.payload.ok) {
          pending.resolve(msg.payload.data);
        } else {
          pending.reject(new Error(msg.payload.error as string || 'Command failed'));
        }
      }
      return;
    }

    if (msg.type === 'da:nav-change') {
      // During manual takeover, auto-capture snapshots on navigation
      if (this.exploreState === 'manual_takeover') {
        this.captureSnapshot().then((snapshot) => {
          this.saveSnapshot(snapshot);
        }).catch(() => {});
      }
    }
  };

  private loadProductInIframe(): void {
    if (!this.iframe) return;
    // Pass the autonomous-discovery iframe token via iframe.name (not a
    // query param) so it survives SPA URL rewrites — see the matching
    // comment in full-page-demo.ts:loadProductInIframe and the boot()
    // resolver in src/index.ts. Strip da_auto_discover from the URL so a
    // top-level boot inside the iframe never re-enters discovery mode and
    // recurses; the iframe child boots IframeAgentMode purely from
    // window.name.
    const url = new URL(this.config.productUrl);
    url.searchParams.delete('da_auto_discover');
    this.iframe.name = `da_auto_discover_iframe:${this.config.token}`;
    this.iframe.src = url.toString();
  }

  private waitForIframeReady(timeoutMs: number): Promise<void> {
    if (this.iframeReady) return Promise.resolve();
    return new Promise((resolve, reject) => {
      const timer = setTimeout(() => {
        this.readyResolve = null;
        reject(new Error('Iframe ready timeout'));
      }, timeoutMs);
      this.readyResolve = () => {
        clearTimeout(timer);
        resolve();
      };
    });
  }

  private sendIframeCommand(type: string, payload: Record<string, unknown>, timeoutMs = 15000): Promise<unknown> {
    return new Promise((resolve, reject) => {
      if (!this.iframe?.contentWindow) {
        reject(new Error('Iframe not available'));
        return;
      }

      const id = `auto_${++this.commandIdCounter}_${Date.now().toString(36)}`;
      const timer = setTimeout(() => {
        this.pendingCommands.delete(id);
        reject(new Error(`Command ${type} timed out`));
      }, timeoutMs);

      this.pendingCommands.set(id, { resolve, reject, timer });

      const msg: DAMessage = {
        __da: true,
        id,
        gen: this.currentGen,
        ts: Date.now(),
        dir: 'parent-to-iframe',
        type,
        payload,
      };

      this.iframe.contentWindow.postMessage(msg, '*');
    });
  }

  private async captureSnapshot(): Promise<PageSnapshot> {
    const data = await this.sendIframeCommand('da:capture-snapshot', {}, 8000);
    return data as PageSnapshot;
  }

  // ─── Action Execution ──────────────────────────────────────────────────

  private async executeAction(action: ExploreAction, currentSnapshot: PageSnapshot): Promise<void> {
    switch (action.type) {
      case 'click':
        await this.sendIframeCommand('da:click', {
          selector: action.selector,
          cursorAnimate: true,
        });
        break;

      case 'navigate': {
        // Handle click: pseudo-hrefs (sidebar items that need clicking instead of URL navigation)
        if (action.url?.startsWith('click:')) {
          const selector = action.url.slice(6); // Remove 'click:' prefix
          try {
            await this.sendIframeCommand('da:click', { selector, cursorAnimate: true }, 5000);
          } catch {
            console.warn('[AutoDiscovery] Click-nav failed for selector:', selector);
          }
          // Wait for SPA navigation to settle, then re-check iframe readiness
          await this.delay(2000);
          break;
        }

        const url = this.resolveUrl(action.url!, currentSnapshot);
        await this.sendIframeCommand('da:navigate', { url });
        // Wait for iframe to be ready after navigation
        this.iframeReady = false;
        try {
          await this.waitForIframeReady(10000);
        } catch {
          // MPA navigation might reinit the controller
          await this.delay(3000);
        }
        break;
      }

      case 'scroll_to':
        if (action.selector && action.selector !== 'body') {
          await this.sendIframeCommand('da:scroll-to', { selector: action.selector });
        } else {
          // General scroll down
          await this.sendIframeCommand('da:exec-action', {
            name: 'scroll-down',
            params: {},
          }).catch(() => {
            // Fallback: just wait a bit
          });
        }
        break;

      case 'type':
        await this.sendIframeCommand('da:type', {
          selector: action.selector,
          text: action.text,
          speed: 50,
        });
        break;

      case 'press_key':
        await this.sendIframeCommand('da:press-key', { key: action.key }, 3000);
        break;

      case 'wait':
        await this.delay(action.ms ?? 1000);
        break;
    }
  }

  // ─── API Calls ──────────────────────────────────────────────────────────

  private async callAutoExplore(snapshot: PageSnapshot, explorationPhase: 'scan' | 'deep_dive' = 'scan'): Promise<Record<string, unknown>> {
    // Differential context: only send new findings in full, compact summary for old ones
    const newFindings = this.findings.slice(this.lastSyncedFindingIndex);
    const previousFindingsCount = this.lastSyncedFindingIndex;
    const previousHighCount = this.findings.slice(0, this.lastSyncedFindingIndex)
      .filter((f) => f.importance === 'high').length;

    const resp = await fetch(`${this.config.apiUrl}/api/v1/discover/auto-explore`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: this.config.sessionId,
        token: this.config.token,
        currentSnapshot: snapshot,
        draftPdp: this.draftPdp,
        exploredPages: Array.from(this.exploredPages.entries()).map(([urlPath, data]) => ({
          urlPath,
          ...data,
        })),
        pendingNavTargets: this.pendingNavTargets.slice(0, 10),
        pendingNavTargetCount: this.pendingNavTargets.length,
        findings: newFindings,
        previousFindingsSummary: previousFindingsCount > 0
          ? `${previousFindingsCount} findings already recorded (${previousHighCount} high-importance)`
          : undefined,
        iterationCount: this.iterationCount,
        timeElapsedMs: Date.now() - this.startTime,
        explorationPhase,
        pageScores: explorationPhase === 'deep_dive' ? this.pageScores : undefined,
        deepDiveTargets: explorationPhase === 'deep_dive' ? this.deepDiveTargets : undefined,
        deepDiveCurrentPage: explorationPhase === 'deep_dive' ? this.deepDiveCurrentPage : undefined,
        deepDiveIterationsOnPage: explorationPhase === 'deep_dive' ? this.deepDiveIterationsOnPage : undefined,
      }),
    });

    if (!resp.ok) {
      let errorDetail = `${resp.status}`;
      try {
        const body = await resp.json();
        if (body.error) errorDetail = `${resp.status}: ${body.error}`;
      } catch {
        // response wasn't JSON
      }
      throw new Error(`Auto-explore failed (${errorDetail})`);
    }

    return resp.json();
  }

  private async callAutoRefine(questionId: string, answer: string): Promise<Record<string, unknown>> {
    const resp = await fetch(`${this.config.apiUrl}/api/v1/discover/auto-refine`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: this.config.sessionId,
        token: this.config.token,
        questionId,
        answer,
      }),
    });

    if (!resp.ok) {
      throw new Error(`Auto-refine failed: ${resp.status}`);
    }

    return resp.json();
  }

  private async callGenerateQuestions(): Promise<Question[]> {
    const resp = await fetch(`${this.config.apiUrl}/api/v1/discover/generate-questions`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: this.config.sessionId,
        token: this.config.token,
        draftPdp: this.draftPdp,
        findings: this.findings,
        exploredPages: Array.from(this.exploredPages.entries()).map(([urlPath, data]) => ({
          urlPath,
          ...data,
        })),
      }),
    });

    if (!resp.ok) {
      throw new Error(`Generate questions failed: ${resp.status}`);
    }

    const data = await resp.json();
    return (data.questions as Question[]) || [];
  }

  private async saveSnapshot(snapshot: PageSnapshot): Promise<void> {
    await fetch(`${this.config.apiUrl}/api/v1/discover/snapshot`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({
        sessionId: this.config.sessionId,
        token: this.config.token,
        snapshot,
      }),
    }).catch(() => {});
  }

  // ─── Helpers ────────────────────────────────────────────────────────────

  private resolveUrl(path: string, snapshot?: PageSnapshot): string {
    if (path.startsWith('http://') || path.startsWith('https://')) return path;
    try {
      const base = snapshot?.urlPath
        ? new URL(snapshot.urlPath, this.config.productUrl).origin
        : this.config.productUrl;
      return new URL(path, base).toString();
    } catch {
      return `${this.config.productUrl}${path}`;
    }
  }

  /** Normalize URL patterns so /users/123 and /users/456 both become /users/:id */
  private normalizeUrlPattern(urlPath: string): string {
    // Replace UUID segments: /users/550e8400-e29b-... → /users/:id
    let normalized = urlPath.replace(
      /\/[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}/gi,
      '/:id',
    );
    // Replace numeric segments: /users/123 → /users/:id
    normalized = normalized.replace(/\/\d+(?=\/|$)/g, '/:id');
    return normalized;
  }

  private updateNavTargets(snapshot: PageSnapshot): void {
    for (const nav of snapshot.navigation) {
      const href = nav.href;
      if (!href || href.startsWith('#') || href.startsWith('javascript:')) continue;

      // Handle click: pseudo-hrefs (sidebar items that need to be clicked)
      if (href.startsWith('click:')) {
        if (!this.pendingNavTargets.some((t) => t.href === href)) {
          this.pendingNavTargets.push({ text: nav.text, href });
        }
        continue;
      }

      const resolvedUrl = this.resolveUrl(href, snapshot);
      let urlPath: string;
      try {
        urlPath = new URL(resolvedUrl).pathname;
      } catch {
        continue;
      }

      // Skip if this URL pattern was already explored (e.g., /users/2 after /users/1)
      const pattern = this.normalizeUrlPattern(urlPath);
      if (this.exploredPatterns.has(pattern)) continue;

      if (!this.exploredPages.has(urlPath) &&
          !this.pendingNavTargets.some((t) => t.href === href)) {
        this.pendingNavTargets.push({ text: nav.text, href });
      }
    }
  }

  private removePendingTarget(urlPath: string): void {
    this.pendingNavTargets = this.pendingNavTargets.filter((t) => {
      try {
        const resolved = new URL(t.href, this.config.productUrl).pathname;
        return resolved !== urlPath;
      } catch {
        return true;
      }
    });
  }

  private delay(ms: number): Promise<void> {
    return new Promise((r) => setTimeout(r, ms));
  }

  // ─── UI Rendering ──────────────────────────────────────────────────────

  private renderLayout(): void {
    this.container = document.createElement('div');
    this.container.id = 'da-auto-discovery';
    this.shadow = this.container.attachShadow({ mode: 'closed' });

    const style = document.createElement('style');
    style.textContent = AUTO_DISCOVERY_CSS;

    const layout = document.createElement('div');
    layout.className = 'da-auto';
    layout.innerHTML = `
      <div class="da-auto-iframe-wrap">
        <iframe class="da-auto-iframe" sandbox="allow-same-origin allow-scripts allow-forms allow-popups allow-top-navigation-by-user-activation" allow="clipboard-read; clipboard-write"></iframe>
      </div>
      <div class="da-auto-panel">
        <div class="da-auto-header">
          <div class="da-auto-title">Autonomous Discovery</div>
          <div class="da-auto-progress"></div>
        </div>
        <div class="da-auto-stats">
          <span class="da-auto-stat">Pages: 0</span>
          <span class="da-auto-stat">Elements: 0</span>
          <span class="da-auto-stat">Findings: 0</span>
        </div>
        <div class="da-auto-page-list"></div>
        <div class="da-auto-status">Initializing...</div>
        <div class="da-auto-messages"></div>
        <div class="da-auto-input-area" style="display:none">
          <textarea class="da-auto-input" placeholder="Type your answer..." rows="2"></textarea>
          <button class="da-auto-send">Send</button>
        </div>
        <div class="da-auto-controls">
          <button class="da-auto-btn da-auto-pause">Pause</button>
          <button class="da-auto-btn da-auto-takeover">Take Over</button>
          <button class="da-auto-btn da-auto-finish">Finish Now</button>
        </div>
      </div>
    `;

    this.shadow.appendChild(style);
    this.shadow.appendChild(layout);
    document.body.appendChild(this.container);

    // Cache refs
    this.iframe = layout.querySelector('.da-auto-iframe')!;
    this.messagesEl = layout.querySelector('.da-auto-messages')!;
    this.statusEl = layout.querySelector('.da-auto-status')!;
    this.progressEl = layout.querySelector('.da-auto-progress')!;
    this.pageListEl = layout.querySelector('.da-auto-page-list')!;
    this.controlsEl = layout.querySelector('.da-auto-controls')!;
    this.inputArea = layout.querySelector('.da-auto-input-area')!;
    this.inputEl = layout.querySelector('.da-auto-input')!;

    // Events
    layout.querySelector('.da-auto-send')!.addEventListener('click', () => this.handleUserSend());
    this.inputEl!.addEventListener('keydown', (e: KeyboardEvent) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        e.preventDefault();
        this.handleUserSend();
      }
    });

    layout.querySelector('.da-auto-pause')!.addEventListener('click', () => this.handlePauseResume());
    layout.querySelector('.da-auto-takeover')!.addEventListener('click', () => this.handleTakeOver());
    layout.querySelector('.da-auto-finish')!.addEventListener('click', () => this.handleFinishNow());
  }

  private showInputArea(): void {
    if (this.inputArea) this.inputArea.style.display = '';
  }

  private addAgentMessage(text: string): void {
    if (!this.messagesEl) return;
    const div = document.createElement('div');
    div.className = 'da-auto-msg da-auto-msg-agent';
    div.textContent = text;
    this.messagesEl.appendChild(div);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  private addUserMessage(text: string): void {
    if (!this.messagesEl) return;
    const div = document.createElement('div');
    div.className = 'da-auto-msg da-auto-msg-user';
    div.textContent = text;
    this.messagesEl.appendChild(div);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  private addSystemMessage(text: string): void {
    if (!this.messagesEl) return;
    const div = document.createElement('div');
    div.className = 'da-auto-msg da-auto-msg-system';
    div.textContent = text;
    this.messagesEl.appendChild(div);
    this.messagesEl.scrollTop = this.messagesEl.scrollHeight;
  }

  private updateStatus(text: string): void {
    if (this.statusEl) this.statusEl.textContent = text;
  }

  private updateProgress(mins: number, secs: number): void {
    if (!this.progressEl) return;
    const maxMins = Math.floor(this.maxExploreTimeMs / 60000);
    const pct = Math.min(100, ((mins * 60 + secs) / (maxMins * 60)) * 100);
    const phaseLabel = this.phase === 'scanning' ? 'Scanning' :
                       this.phase === 'deep_diving' ? 'Deep Diving' :
                       this.phase === 'questions' ? 'Q&A' : this.phase;
    this.progressEl.innerHTML = `
      <div class="da-auto-timer">${phaseLabel} — ${mins}:${secs.toString().padStart(2, '0')} / ~${maxMins}:00</div>
      <div class="da-auto-bar"><div class="da-auto-bar-fill" style="width: ${pct}%"></div></div>
    `;

    // Update stats
    const statsEl = this.shadow?.querySelector('.da-auto-stats');
    if (statsEl) {
      const elemCount = Array.from(this.exploredPages.values()).reduce((s, p) => s + p.elementCount, 0);
      statsEl.innerHTML = `
        <span class="da-auto-stat">Pages: ${this.exploredPages.size}</span>
        <span class="da-auto-stat">Elements: ${elemCount}</span>
        <span class="da-auto-stat">Findings: ${this.findings.length}</span>
      `;
    }
  }

  private updatePageList(): void {
    if (!this.pageListEl) return;
    let html = '';
    for (const [urlPath, data] of this.exploredPages) {
      html += `<div class="da-auto-page-item da-auto-page-done">&#x2713; ${data.title || urlPath}</div>`;
    }
    for (const nav of this.pendingNavTargets.slice(0, 5)) {
      html += `<div class="da-auto-page-item da-auto-page-pending">&#x25CB; ${nav.text}</div>`;
    }
    this.pageListEl.innerHTML = html;

    // Also update stats counters
    const statsEl = this.shadow?.querySelector('.da-auto-stats');
    if (statsEl) {
      const elemCount = Array.from(this.exploredPages.values()).reduce((s, p) => s + p.elementCount, 0);
      statsEl.innerHTML = `
        <span class="da-auto-stat">Pages: ${this.exploredPages.size}</span>
        <span class="da-auto-stat">Elements: ${elemCount}</span>
        <span class="da-auto-stat">Findings: ${this.findings.length}</span>
      `;
    }
  }

  private updateControls(): void {
    if (!this.controlsEl) return;
    const pauseBtn = this.controlsEl.querySelector('.da-auto-pause') as HTMLButtonElement;
    const takeoverBtn = this.controlsEl.querySelector('.da-auto-takeover') as HTMLButtonElement;
    const finishBtn = this.controlsEl.querySelector('.da-auto-finish') as HTMLButtonElement;

    if (this.phase === 'scanning' || this.phase === 'deep_diving') {
      pauseBtn.style.display = '';
      takeoverBtn.style.display = '';
      finishBtn.style.display = '';
      pauseBtn.textContent = this.exploreState === 'paused' ? 'Resume' : 'Pause';
      takeoverBtn.textContent = this.exploreState === 'manual_takeover' ? 'Hand Back' : 'Take Over';
    } else if (this.phase === 'questions') {
      pauseBtn.style.display = 'none';
      takeoverBtn.style.display = 'none';
      finishBtn.style.display = '';
      finishBtn.textContent = 'Finish Now';
    } else if (this.phase === 'finalizing' || this.phase === 'complete') {
      pauseBtn.style.display = 'none';
      takeoverBtn.style.display = 'none';
      finishBtn.style.display = 'none';
    }
  }

  // ─── Control Handlers ──────────────────────────────────────────────────

  private handlePauseResume(): void {
    if (this.exploreState === 'paused') {
      this.exploreState = 'running';
      this.updateControls();
      this.addSystemMessage('Resuming exploration...');
      this.exploreLoop();
    } else {
      this.exploreState = 'paused';
      this.updateControls();
      this.addSystemMessage('Exploration paused. Click Resume to continue.');
    }
  }

  private handleTakeOver(): void {
    if (this.exploreState === 'manual_takeover') {
      // Hand back
      this.exploreState = 'running';
      if (this.iframe) this.iframe.style.pointerEvents = 'none';
      this.updateControls();
      this.addSystemMessage('Resuming autonomous exploration...');
      this.exploreLoop();
    } else {
      // Take over
      this.exploreState = 'manual_takeover';
      if (this.iframe) this.iframe.style.pointerEvents = 'auto';
      this.updateControls();
      this.addSystemMessage('You are now in control. Click around your product freely. Click "Hand Back" when done.');
    }
  }

  private async handleFinishNow(): Promise<void> {
    if (this.phase === 'scanning' || this.phase === 'deep_diving') {
      // Force transition to questions
      this.exploreState = 'paused';
      await this.transitionToQuestions();
    } else if (this.phase === 'questions') {
      // Skip remaining questions and finalize
      await this.handleFinalize();
    }
  }
}

// ─── CSS ────────────────────────────────────────────────────────────────

const AUTO_DISCOVERY_CSS = `
  :host { all: initial; }

  .da-auto {
    position: fixed;
    inset: 0;
    display: flex;
    z-index: 2147483647;
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 13px;
    color: #1a1a2e;
    background: #f8f9fb;
  }

  .da-auto-iframe-wrap {
    flex: 1;
    min-width: 0;
    position: relative;
    background: #fff;
  }

  .da-auto-iframe {
    width: 100%;
    height: 100%;
    border: none;
    pointer-events: none; /* Agent controls by default */
  }

  .da-auto-panel {
    width: 340px;
    min-width: 280px;
    display: flex;
    flex-direction: column;
    background: #fff;
    border-left: 1px solid #e2e4ea;
    overflow: hidden;
  }

  .da-auto-header {
    padding: 14px 16px 10px;
    border-bottom: 1px solid #e2e4ea;
    background: #f8f9fb;
  }

  .da-auto-title {
    font-weight: 600;
    font-size: 14px;
    color: #1a1a2e;
    margin-bottom: 6px;
  }

  .da-auto-progress {
    min-height: 24px;
  }

  .da-auto-timer {
    font-size: 11px;
    color: #6b7280;
    margin-bottom: 4px;
  }

  .da-auto-bar {
    height: 4px;
    background: #e5e7eb;
    border-radius: 2px;
    overflow: hidden;
  }

  .da-auto-bar-fill {
    height: 100%;
    background: #6366f1;
    border-radius: 2px;
    transition: width 0.5s ease;
  }

  .da-auto-stats {
    display: flex;
    gap: 12px;
    padding: 8px 16px;
    font-size: 11px;
    color: #6b7280;
    border-bottom: 1px solid #f0f1f3;
  }

  .da-auto-stat {
    white-space: nowrap;
  }

  .da-auto-page-list {
    padding: 8px 16px;
    max-height: 120px;
    overflow-y: auto;
    border-bottom: 1px solid #f0f1f3;
  }

  .da-auto-page-item {
    font-size: 12px;
    padding: 2px 0;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
  }

  .da-auto-page-done { color: #059669; }
  .da-auto-page-pending { color: #9ca3af; }

  .da-auto-status {
    padding: 8px 16px;
    font-size: 12px;
    color: #6366f1;
    font-style: italic;
    border-bottom: 1px solid #f0f1f3;
    min-height: 32px;
  }

  .da-auto-messages {
    flex: 1;
    overflow-y: auto;
    padding: 12px 16px;
    display: flex;
    flex-direction: column;
    gap: 8px;
  }

  .da-auto-msg {
    padding: 8px 12px;
    border-radius: 10px;
    max-width: 90%;
    line-height: 1.4;
    white-space: pre-wrap;
    word-wrap: break-word;
  }

  .da-auto-msg-agent {
    background: #eef0ff;
    color: #1a1a2e;
    align-self: flex-start;
    border-bottom-left-radius: 2px;
  }

  .da-auto-msg-user {
    background: #6366f1;
    color: #fff;
    align-self: flex-end;
    border-bottom-right-radius: 2px;
  }

  .da-auto-msg-system {
    background: #f3f4f6;
    color: #6b7280;
    align-self: center;
    font-size: 11px;
    text-align: center;
  }

  .da-auto-input-area {
    display: flex;
    gap: 8px;
    padding: 10px 16px;
    border-top: 1px solid #e2e4ea;
    background: #f8f9fb;
  }

  .da-auto-input {
    flex: 1;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    padding: 8px 10px;
    font-size: 13px;
    font-family: inherit;
    resize: none;
    outline: none;
    min-height: 36px;
  }

  .da-auto-input:focus {
    border-color: #6366f1;
    box-shadow: 0 0 0 2px rgba(99,102,241,0.1);
  }

  .da-auto-send {
    padding: 8px 16px;
    background: #6366f1;
    color: #fff;
    border: none;
    border-radius: 8px;
    font-size: 13px;
    font-weight: 500;
    cursor: pointer;
    white-space: nowrap;
  }

  .da-auto-send:hover { background: #4f46e5; }

  .da-auto-controls {
    display: flex;
    gap: 8px;
    padding: 10px 16px;
    border-top: 1px solid #e2e4ea;
  }

  .da-auto-btn {
    flex: 1;
    padding: 8px 12px;
    border: 1px solid #d1d5db;
    border-radius: 8px;
    background: #fff;
    font-size: 12px;
    font-weight: 500;
    cursor: pointer;
    text-align: center;
  }

  .da-auto-btn:hover { background: #f3f4f6; }

  .da-auto-finish {
    background: #6366f1;
    color: #fff;
    border-color: #6366f1;
  }

  .da-auto-finish:hover { background: #4f46e5; }
`;
